import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Swords, Calendar, MapPin, Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import type { DiscoveryMatch } from '@/hooks/useDiscoveryEngine';

interface Props {
  matches: DiscoveryMatch[];
}

export const DiscoverMatchesWidget: React.FC<Props> = ({ matches }) => {
  const navigate = useNavigate();

  if (matches.length === 0) return null;

  return (
    <Card className="border-border">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center">
          <Swords className="h-4 w-4 mr-2 text-primary" />
          Matches For You
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {matches.map((match) => (
          <div
            key={match.id}
            className="p-3 rounded-lg border border-border hover:bg-muted/50 cursor-pointer transition-colors"
            onClick={() => navigate(`/matches/${match.id}`)}
          >
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-2 text-sm">
                <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="font-medium">
                  {match.date ? format(new Date(match.date), 'MMM d, HH:mm') : 'TBD'}
                </span>
              </div>
              <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                {match.spotsAvailable} spot{match.spotsAvailable !== 1 ? 's' : ''} left
              </Badge>
            </div>
            {match.clubName && (
              <div className="flex items-center text-xs text-muted-foreground mb-1.5">
                <MapPin className="h-3 w-3 mr-1" />
                <span className="truncate">{match.clubName}</span>
              </div>
            )}
            <div className="flex flex-wrap gap-1">
              {match.matchReasons.slice(0, 2).map((reason) => (
                <Badge key={reason} variant="secondary" className="text-[10px] px-1.5 py-0">
                  {reason}
                </Badge>
              ))}
              <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                {Math.round(match.score * 100)}% fit
              </Badge>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};
